import type { CatalogCountry, Gender } from '@/api/types';

/** Regional-indicator flag for an ISO-3166-1 alpha-2 code. */
export function flagEmoji(code: string | null | undefined): string {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return '';
  const base = 0x1f1e6;
  const up = code.toUpperCase();
  return String.fromCodePoint(base + up.charCodeAt(0) - 65, base + up.charCodeAt(1) - 65);
}

const CODES =
  'AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ CA CD CF CG CH CI CK CL CM CN CO CR CU CV CW CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW'.split(' ');

let fallback: CatalogCountry[] | null = null;

/** Used only if the catalog doesn't carry countries (older API). */
export function fallbackCountries(): CatalogCountry[] {
  if (fallback) return fallback;
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    names = null;
  }
  fallback = CODES.map((code) => ({ code, name: names?.of(code) ?? code, flag: flagEmoji(code) })).sort((a, b) => a.name.localeCompare(b.name));
  return fallback;
}

export function countryList(fromCatalog: CatalogCountry[] | undefined | null): CatalogCountry[] {
  return fromCatalog?.length ? fromCatalog : fallbackCountries();
}

export function countryName(code: string | null | undefined, list: CatalogCountry[]): string {
  if (!code) return '';
  return list.find((c) => c.code === code)?.name ?? code;
}

export function searchCountries(list: CatalogCountry[], q: string): CatalogCountry[] {
  const s = q.trim().toLowerCase();
  if (!s) return list;
  return list.filter((c) => c.name.toLowerCase().includes(s) || c.code.toLowerCase() === s);
}

export const GENDERS: { id: Gender; label: string }[] = [
  { id: 'woman', label: 'Woman' },
  { id: 'man', label: 'Man' },
  { id: 'nonbinary', label: 'Non-binary' },
  { id: 'unspecified', label: 'Prefer not to say' },
];

export function genderList(fromCatalog: { id: Gender; label: string }[] | undefined | null) {
  return fromCatalog?.length ? fromCatalog : GENDERS;
}

/** Gender filter options never include 'unspecified' (SPEC_V2 §4). */
export const FILTER_GENDER_LABEL: Record<Exclude<Gender, 'unspecified'>, string> = { woman: 'Women', man: 'Men', nonbinary: 'Non-binary people' };
export const MAX_FILTER_COUNTRIES = 5;
